import { create } from 'zustand';

import { CandidateUI } from '@/api/matchmaker/types.ts';
import MatchMakerService from '@/api/matchmaker/matchmaker.ts';
import RelationController from '@/api/relation/relation.ts';
import { ReportReason } from '@/enums/apiEnums.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import {
  isCustomCard,
  customCardsConfig
} from '@/components/Discovery/Card/CustomCardsConfig.ts';

interface CandidatesStore {
  candidates: CandidateUI[];
  fetchLimit: number;
  isEmpty: boolean;
  isGeoAccessRequested: boolean;
  isGeoAccessGranted: boolean;
  isLoading: boolean;
  isMatchPopupOpen: boolean;
  fetchCandidates: () => Promise<void>;
  blockAndRemoveCandidate: (hid: string, reason: ReportReason) => Promise<void>;
  removeCandidate: (hid: string) => Promise<void>;
  removeAllCandidates: () => void;
  expireTime: number | null;
  isExpired: () => boolean;
  resetExpired: () => void;
  updateCandidateLikesMe: (hid: string) => void;
  filterCandidates: (hid: string) => void;
  openMatchPopup: (open: boolean) => void;
  geoAccessRequested: (requested: boolean) => void;
  geoAccessGranted: (granted: boolean) => void;
}

const insertCustomCards = async (
  candidates: CandidateUI[]
): Promise<CandidateUI[]> => {
  let result = [...candidates];
  for (const config of customCardsConfig) {
    if (await config.insertCondition(result)) {
      const placeholder = config.createPlaceholder();
      const insertIndex = config.insertPosition(result);
      result = [
        ...result.slice(0, insertIndex),
        placeholder,
        ...result.slice(insertIndex)
      ];
    }
  }
  return result;
};

export const useDiscoveryStore = create<CandidatesStore>((set, get) => {
  return {
    candidates: [],
    fetchLimit: 10,
    isEmpty: false,
    isGeoAccessRequested: false,
    isGeoAccessGranted: false,
    isLoading: false,
    isMatchPopupOpen: false,
    expireTime: null,
    fetchCandidates: async () => {
      const { fetchLimit } = get();
      set({ isLoading: true });

      try {
        const candidates = await MatchMakerService.fetchMatchCandidates({
          limit: fetchLimit
        });

        if (Array.isArray(candidates) && candidates.length === 0) {
          set({ isEmpty: true, candidates: [] });
        } else {
          const modifiedCandidates = await insertCustomCards(candidates);
          set({
            candidates: modifiedCandidates,
            isEmpty: false,
            expireTime: Date.now() + 3 * 60 * 1000
          });
        }
      } catch (e) {
        catchErrors(e, true);
      } finally {
        setTimeout(() => set({ isLoading: false }), 500);
      }
    },
    blockAndRemoveCandidate: async (hid: string, reason: ReportReason) => {
      try {
        await RelationController.blockUser(hid, reason);
        const { removeCandidate } = get();
        await removeCandidate(hid);
      } catch (e) {
        catchErrors(e, true);
      }
    },
    removeCandidate: async (hid: string) => {
      const { fetchLimit } = get();
      const halfLength = Math.ceil(fetchLimit / 2);

      set((state) => {
        const filteredCandidates = state.candidates.filter(
          (c) => c.profile.hid !== hid
        );
        return {
          candidates: filteredCandidates,
          isEmpty: filteredCandidates.length === 0
        };
      });

      if (isCustomCard(hid)) {
        return;
      }

      const { candidates } = get();

      try {
        // Check if remaining items are at 50% of the fetch limit
        if (candidates.length <= halfLength) {
          const existingHids = candidates
            .filter((c) => !isCustomCard(c.profile.hid))
            .map((c) => c.profile.hid);

          const newCandidates = await MatchMakerService.fetchMatchCandidates({
            limit: fetchLimit,
            exclude_user_hids: existingHids
          });

          const modifiedCandidates = await insertCustomCards([
            ...newCandidates,
            ...candidates.filter((c) => !isCustomCard(c.profile.hid))
          ]);
          set({
            candidates: modifiedCandidates,
            isEmpty: modifiedCandidates.length === 0
          });
        }
      } catch (e) {
        catchErrors(e, true);
      }
    },
    removeAllCandidates: () => set({ candidates: [], isEmpty: true }),
    isExpired: () => {
      const { expireTime } = get();
      return !expireTime || Date.now() > expireTime;
    },
    resetExpired: () => {
      set({ expireTime: Date.now() });
    },
    updateCandidateLikesMe: (hid) => {
      set((state) => {
        const mapped = state.candidates.map((c) => {
          if (c.profile.hid === hid) {
            return { ...c, likesMe: true };
          }
          return c;
        });
        return { candidates: mapped };
      });
    },
    filterCandidates: (hid) => {
      set((state) => {
        const filtered = state.candidates.filter((c) => c.profile.hid !== hid);
        return { candidates: filtered, isEmpty: filtered.length === 0 };
      });
    },
    openMatchPopup: (open) => {
      set({ isMatchPopupOpen: open });
    },
    geoAccessRequested: (requested) => {
      set({ isGeoAccessRequested: requested });
    },
    geoAccessGranted: (granted) => {
      set({ isGeoAccessGranted: granted });
    }
  };
});
